import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import ImageUpload from "@/components/ImageUpload";
import { useAuth } from "@/contexts/AuthContext";
import {
  setupProfile,
  requestAndUpdateGPS,
  ipLocationFallback,
  getCountries,
  api,
} from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowLeft,
  ArrowRight,
  MapPin,
  Gift,
  Check,
  Loader2,
  Phone,
  MessageCircle,
  ShieldCheck,
  SkipForward,
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const STEPS = [
  "Location",
  "About You",
  "Health & Lifestyle",
  "Appearance & Discovery",
  "WhatsApp Number",
];

const AGE_OPTIONS = Array.from({ length: 83 }, (_, i) => i + 18);

function birthYearFromDob(dob) {
  if (!dob) return null;
  const parsed = new Date(dob);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.getFullYear();
}

function computedAgeFromDob(dob) {
  const by = birthYearFromDob(dob);
  if (by == null) return null;
  return new Date().getFullYear() - by;
}

const COUNTRY_CODES = [
  { code: "+27", name: "South Africa", flag: "🇿🇦", min: 9, max: 9 },
  { code: "+1", name: "United States", flag: "🇺🇸", min: 10, max: 10 },
  { code: "+44", name: "United Kingdom", flag: "🇬🇧", min: 10, max: 10 },
  { code: "+61", name: "Australia", flag: "🇦🇺", min: 9, max: 9 },
  { code: "+263", name: "Zimbabwe", flag: "🇿🇼", min: 9, max: 9 },
  { code: "+260", name: "Zambia", flag: "🇿🇲", min: 9, max: 9 },
  { code: "+267", name: "Botswana", flag: "🇧🇼", min: 8, max: 8 },
  { code: "+264", name: "Namibia", flag: "🇳🇦", min: 8, max: 8 },
  { code: "+266", name: "Lesotho", flag: "🇱🇸", min: 8, max: 8 },
  { code: "+268", name: "Eswatini", flag: "🇸🇿", min: 8, max: 8 },
  { code: "+258", name: "Mozambique", flag: "🇲🇿", min: 9, max: 9 },
  { code: "+265", name: "Malawi", flag: "🇲🇼", min: 9, max: 9 },
  { code: "+254", name: "Kenya", flag: "🇰🇪", min: 9, max: 9 },
  { code: "+234", name: "Nigeria", flag: "🇳🇬", min: 10, max: 10 },
  { code: "+233", name: "Ghana", flag: "🇬🇭", min: 9, max: 9 },
  { code: "+91", name: "India", flag: "🇮🇳", min: 10, max: 10 },
  { code: "+49", name: "Germany", flag: "🇩🇪", min: 10, max: 11 },
  { code: "+33", name: "France", flag: "🇫🇷", min: 9, max: 9 },
  { code: "+31", name: "Netherlands", flag: "🇳🇱", min: 9, max: 9 },
  { code: "+64", name: "New Zealand", flag: "🇳🇿", min: 9, max: 10 },
];

export default function ProfileSetup() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [countries, setCountries] = useState([]);
  const [locationSet, setLocationSet] = useState(false);
  const [locationLoading, setLocationLoading] = useState(false);
  const [locationText, setLocationText] = useState("Set your location");

  const [inviteCode, setInviteCode] = useState("");
  const [inviteApplied, setInviteApplied] = useState(false);
  const [inviteLoading, setInviteLoading] = useState(false);
  const showInvite = user?.invite_code_used === false;

  const [dateOfBirth, setDateOfBirth] = useState("");
  const [selectedAge, setSelectedAge] = useState(null);
  const [prefGenderUserOverridden, setPrefGenderUserOverridden] = useState(false);
  const [gender, setGender] = useState("");
  const [healthStatus, setHealthStatus] = useState("");
  const [sexualOrientation, setSexualOrientation] = useState("");
  const [education, setEducation] = useState("");
  const [kids, setKids] = useState("");
  const [wantKids, setWantKids] = useState("");
  const [smoke, setSmoke] = useState("");
  const [drink, setDrink] = useState("");
  const [employment, setEmployment] = useState("");
  const [displayName, setDisplayName] = useState(user?.name || "");
  const [bio, setBio] = useState("");
  const [interests, setInterests] = useState("");
  const [lookingFor, setLookingFor] = useState("");
  const [profileImage, setProfileImage] = useState(user?.picture || "");
  const [galleryImages, setGalleryImages] = useState([]);
  const [prefGender, setPrefGender] = useState("");
  const [prefMinAge, setPrefMinAge] = useState(18);
  const [prefMaxAge, setPrefMaxAge] = useState(99);
  const [prefCountry, setPrefCountry] = useState("");
  const [prefMaxDistance, setPrefMaxDistance] = useState(15000);
  const [prefHealthStatus, setPrefHealthStatus] = useState("");
  const [positiveSince, setPositiveSince] = useState("");
  const [height, setHeight] = useState("");
  const [ethnicity, setEthnicity] = useState("");
  const [religion, setReligion] = useState("");

  const [countryCode, setCountryCode] = useState("+27");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [phoneError, setPhoneError] = useState("");

  // Safety-net consent dialog state. With the new AcceptLegal.jsx flow,
  // consent is recorded before this screen is reachable. This dialog
  // only triggers if something wiped the consent state between accept
  // and submit — rare but recoverable without an unrecoverable dead-end.
  const [consentDialogOpen, setConsentDialogOpen] = useState(false);
  const [consentSubmitting, setConsentSubmitting] = useState(false);
  const [consentErrorMessage, setConsentErrorMessage] = useState("");
  const [pendingSkipPhone, setPendingSkipPhone] = useState(false);

  const today = new Date();
  const maxDateForAge = new Date(
    today.getFullYear() - 18,
    today.getMonth(),
    today.getDate(),
  )
    .toISOString()
    .split("T")[0];
  const minDateForAge = new Date(
    today.getFullYear() - 100,
    today.getMonth(),
    today.getDate(),
  )
    .toISOString()
    .split("T")[0];

  useEffect(() => {
    getCountries()
      .then((data) => {
        if (Array.isArray(data)) setCountries(data.map((c) => c.name).sort());
      })
      .catch(() => {});
  }, []);

  // Defensive: if a user who already completed onboarding lands here
  // (stale bookmark, browser back-button, or a stale redirect from a
  // prior build), bounce them to /find instead of making them redo
  // onboarding. The `!saving` guard prevents this from firing while a
  // submit is in flight.
  useEffect(() => {
    if (user?.onboarding_complete === true && !saving) {
      navigate("/find", { replace: true });
    }
  }, [user?.onboarding_complete, saving, navigate]);

  const handleGenderChange = (g) => {
    const selected = gender === g ? "" : g;
    setGender(selected);
    if (!prefGenderUserOverridden && selected) {
      if (selected === "Male") setPrefGender("Female");
      else if (selected === "Female") setPrefGender("Male");
      else setPrefGender("");
    }
  };

  const canGoNext = () => {
    if (step === 0) {
      if (!locationSet) {
        toast.error("Please set your location before continuing");
        return false;
      }
      return true;
    }

    if (step === 1) {
      if (!dateOfBirth.trim()) {
        toast.error("Please select your date of birth");
        return false;
      }
      const computed = computedAgeFromDob(dateOfBirth);
      if (computed == null) {
        toast.error("Invalid date of birth");
        return false;
      }
      if (computed < 18) {
        toast.error("You must be at least 18 years old");
        return false;
      }
      if (computed > 100) {
        toast.error("Age must be 100 or less");
        return false;
      }
      if (selectedAge == null) {
        toast.error("Please select your age");
        return false;
      }
      if (selectedAge !== computed) {
        toast.error(
          `The age you selected (${selectedAge}) does not match the age ` +
            `calculated from your date of birth (${computed}). Please select ` +
            `the correct age — it is used to find you the right matches.`,
          { duration: 5000 },
        );
        return false;
      }
      if (!gender) {
        toast.error("Please select your gender");
        return false;
      }
      if (!healthStatus) {
        toast.error(
          "Please select your health status — this is required for matching",
        );
        return false;
      }
      if (!sexualOrientation) {
        toast.error("Please select your sexual orientation");
        return false;
      }
      return true;
    }

    if (step === 2) {
      if (!ethnicity) {
        toast.error("Please select your ethnicity");
        return false;
      }
      return true;
    }

    return true;
  };

  const handleNext = () => {
    if (!canGoNext()) return;
    if (step < STEPS.length - 1) setStep(step + 1);
  };

  const handleBack = () => {
    if (step > 0) setStep(step - 1);
  };

  const validatePhone = () => {
    if (!phoneNumber.trim()) return true;
    const selectedCountry = COUNTRY_CODES.find((c) => c.code === countryCode);
    if (!selectedCountry) {
      setPhoneError("Please select a valid country code.");
      return false;
    }
    const digits = phoneNumber.replace(/\D/g, "");
    if (
      digits.length < selectedCountry.min ||
      digits.length > selectedCountry.max
    ) {
      setPhoneError(
        `Number should be ${selectedCountry.min}-${selectedCountry.max} digits for ${selectedCountry.name}.`,
      );
      return false;
    }
    setPhoneError("");
    return true;
  };

  const _doSubmit = async (skipPhone) => {
    setSaving(true);
    try {
      const payload = {
        date_of_birth: dateOfBirth,
        gender,
        health_status: healthStatus,
        sexual_orientation: sexualOrientation,
        education,
        kids,
        want_kids: wantKids,
        smoke,
        drink,
        employment,
        display_name: displayName,
        bio,
        interests,
        looking_for: lookingFor,
        profile_image: profileImage,
        gallery_images: galleryImages,
        pref_gender: prefGender,
        pref_min_age: prefMinAge,
        pref_max_age: prefMaxAge,
        pref_country: prefCountry,
        pref_max_distance: prefMaxDistance,
        pref_health_status: prefHealthStatus,
        positive_since: positiveSince,
        height,
        ethnicity,
        religion,
        phone_number: skipPhone
          ? ""
          : `${countryCode}${phoneNumber.replace(/\D/g, "")}`,
      };
      await setupProfile(payload);

      if (showInvite && inviteCode.trim() && !inviteApplied) {
        setInviteLoading(true);
        try {
          const res = await api.post("/apply-invite", {
            code: inviteCode.trim().toUpperCase(),
          });
          toast.success(res.data.message || "Invite code applied! +50 tokens.");
          setInviteApplied(true);
        } catch (err) {
          toast.error(err.response?.data?.detail || "Invalid invite code");
        } finally {
          setInviteLoading(false);
        }
      }

      await refresh();
      toast.success("Profile created! Let's go.");
      navigate("/find");
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : detail?.message || "";

      if (status === 403 && /special|consent/i.test(msg)) {
        setSaving(false);
        setPendingSkipPhone(skipPhone);
        setConsentErrorMessage(msg);
        setConsentDialogOpen(true);
        return;
      }

      toast.error(msg || "Something went wrong");
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async (skipPhone = false) => {
    if (!dateOfBirth.trim() || selectedAge == null) {
      toast.error("Please select your date of birth and age");
      return;
    }
    const computed = computedAgeFromDob(dateOfBirth);
    if (computed == null || selectedAge !== computed) {
      toast.error(
        "The age you selected does not match your date of birth. " +
          "Please fix this before continuing.",
        { duration: 5000 },
      );
      return;
    }

    if (!skipPhone && !validatePhone()) return;

    await _doSubmit(skipPhone);
  };

  const handleConsentAccept = async () => {
    setConsentSubmitting(true);
    try {
      const version = user?.current_consent_version;
      if (!version) {
        throw new Error("Missing current consent version");
      }
      await api.post("/accept-special-consent", { version });
      await refresh();
      setConsentDialogOpen(false);
      await _doSubmit(pendingSkipPhone);
    } catch (err) {
      toast.error(
        err.response?.data?.detail ||
          err.message ||
          "Could not record consent. Please try again.",
      );
    } finally {
      setConsentSubmitting(false);
    }
  };

  const skipWhatsApp = () => {
    handleSubmit(true);
  };

  const handleUpdateLocation = async () => {
    setLocationLoading(true);
    try {
      const result = await requestAndUpdateGPS();
      if (result.ok) {
        toast.success("Location updated!", {
          description: `📍 ${result.city}, ${result.country}`,
        });
        setLocationText(`${result.city}, ${result.country}`);
        setLocationSet(true);
        return;
      }
      throw new Error("GPS failed");
    } catch (gpsError) {
      try {
        const ipResult = await ipLocationFallback();
        if (ipResult.ok) {
          toast.warning("Using approximate location", {
            description: "For better accuracy, please enable GPS",
          });
          const loc = `${ipResult.city || ""} ${ipResult.country || ""}`.trim();
          setLocationText(loc || "Approximate location set");
          setLocationSet(true);
          return;
        }
        throw new Error("IP fallback failed");
      } catch (ipError) {
        toast.error("Unable to set location. Please enable location services.");
      }
    } finally {
      setLocationLoading(false);
    }
  };

  const liveComputedAge = computedAgeFromDob(dateOfBirth);
  const ageMismatch =
    liveComputedAge != null &&
    selectedAge != null &&
    selectedAge !== liveComputedAge;

  return (
    <div className="min-h-screen bg-[#FAFAF7]">
      <Navbar />
      <main className="max-w-2xl mx-auto px-4 py-8">
        {showInvite && (
          <div className="bg-purple-50 border border-purple-200 rounded-2xl p-4 mb-6">
            <div className="flex items-center gap-2 mb-2">
              <Gift className="w-5 h-5 text-purple-600" />
              <h3 className="font-semibold text-purple-900">
                Got an invite code?
              </h3>
            </div>
            <p className="text-sm text-purple-700 mb-3">
              Enter it now and you'll both earn 50 tokens + 1 diamond!
            </p>
            <div className="flex gap-2 items-center">
              <input
                type="text"
                value={inviteCode}
                onChange={(e) => setInviteCode(e.target.value)}
                placeholder="e.g. ABC123"
                maxLength={7}
                className="neo-input flex-1"
                disabled={inviteApplied}
              />
              {inviteApplied ? (
                <span className="text-green-600 flex items-center gap-1 text-sm whitespace-nowrap">
                  <Check className="w-4 h-4" /> Applied
                </span>
              ) : inviteLoading ? (
                <Loader2 className="w-5 h-5 animate-spin text-purple-600" />
              ) : null}
            </div>
            <p className="text-xs text-purple-500 mt-2">
              You can leave this blank if you don't have a code.
            </p>
          </div>
        )}

        <div className="flex items-center gap-2 mb-8">
          {STEPS.map((s, i) => (
            <div key={s} className="flex items-center gap-2 flex-1">
              <div
                className={`h-2 flex-1 rounded-full ${
                  i <= step ? "bg-purple-600" : "bg-[#E7E5E0]"
                }`}
              />
              <span
                className={`text-xs whitespace-nowrap ${
                  i <= step ? "text-purple-600 font-medium" : "text-[#6B6B70]"
                }`}
              >
                {s}
              </span>
            </div>
          ))}
        </div>

        {step === 0 && (
          <div className="bg-white border border-[#E7E5E0] rounded-2xl p-6 text-center space-y-4">
            <MapPin className="w-12 h-12 mx-auto text-purple-600" />
            <h2 className="font-semibold text-lg">
              Enable Location <span className="text-red-500">*</span>
            </h2>
            <p className="text-sm text-[#6B6B70]">
              Your location is required to find people near you. We only share
              approximate distances – never your exact address.
            </p>
            <button
              onClick={handleUpdateLocation}
              disabled={locationLoading}
              className="neo-btn bg-purple-600 border-purple-600 !px-8 !py-3 !text-base !rounded-full"
            >
              {locationLoading
                ? "Updating..."
                : locationSet
                ? locationText
                : "Set your location"}
            </button>
            {locationSet && (
              <p className="text-xs text-green-600 font-medium">
                ✓ Location set – {locationText}
              </p>
            )}
            <p className="text-xs text-[#6B6B70]">
              You cannot proceed without setting a location.
            </p>
          </div>
        )}

        {step === 1 && (
          <div className="bg-white border border-[#E7E5E0] rounded-2xl p-6 space-y-4">
            <h2 className="font-semibold text-lg">About You</h2>

            <div>
              <label className="text-xs font-semibold uppercase">
                Date of Birth <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={dateOfBirth}
                onChange={(e) => setDateOfBirth(e.target.value)}
                className="neo-input mt-1"
                min={minDateForAge}
                max={maxDateForAge}
                required
              />
              <p className="text-xs text-[#6B6B70] mt-1">
                You must be between 18 and 100 years old
              </p>
            </div>

            <div>
              <label className="text-xs font-semibold uppercase">
                Your Age <span className="text-red-500">*</span>
              </label>
              <select
                value={selectedAge ?? ""}
                onChange={(e) =>
                  setSelectedAge(
                    e.target.value ? parseInt(e.target.value, 10) : null,
                  )
                }
                className={`neo-input mt-1 ${
                  ageMismatch ? "border-red-400 ring-1 ring-red-300" : ""
                }`}
              >
                <option value="">Select your age</option>
                {AGE_OPTIONS.map((age) => (
                  <option key={age} value={age}>
                    {age} years old
                  </option>
                ))}
              </select>
              {!ageMismatch &&
                liveComputedAge != null &&
                selectedAge === liveComputedAge && (
                  <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                    <Check className="w-3 h-3" /> Age verified against date of
                    birth
                  </p>
                )}
            </div>

            <div>
              <label className="text-xs font-semibold uppercase">
                Gender <span className="text-red-500">*</span>
              </label>
              <div className="flex gap-2 mt-1 flex-wrap">
                {["Male", "Female", "Non-binary", "Other"].map((g) => (
                  <button
                    key={g}
                    type="button"
                    onClick={() => handleGenderChange(g)}
                    className={`px-3 py-1.5 rounded-full text-sm border ${
                      gender === g
                        ? "bg-purple-600 text-white border-purple-600"
                        : "bg-white border-[#E7E5E0]"
                    }`}
                  >
                    {g}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="text-xs font-semibold uppercase">
                Health Status <span className="text-red-500">*</span>
              </label>
              <p className="text-xs text-[#6B6B70] mt-1 mb-2 leading-relaxed">
                <strong className="text-[#52525B]">
                  Required for matching.
                </strong>{" "}
                Haven is a community for people living with HIV, HSV, HPV, or
                other STIs. We need this to match you with others on similar
                journeys. It is only shown to matches you approve.
              </p>
              <div className="flex gap-2 flex-wrap">
                {["HIV", "HPV", "HSV", "Other STD"].map((h) => (
                  <button
                    key={h}
                    type="button"
                    onClick={() => setHealthStatus(h)}
                    className={`px-3 py-1.5 rounded-full text-sm border ${
                      healthStatus === h
                        ? "bg-purple-600 text-white border-purple-600"
                        : "bg-white border-[#E7E5E0]"
                    }`}
                  >
                    {h}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="text-xs font-semibold uppercase">
                Sexual Orientation <span className="text-red-500">*</span>
              </label>
              <div className="flex gap-2 mt-1 flex-wrap">
                {["Straight", "Gay", "Lesbian", "Bisexual"].map((o) => (
                  <button
                    key={o}
                    type="button"
                    onClick={() => setSexualOrientation(o)}
                    className={`px-3 py-1.5 rounded-full text-sm border ${
                      sexualOrientation === o
                        ? "bg-purple-600 text-white border-purple-600"
                        : "bg-white border-[#E7E5E0]"
                    }`}
                  >
                    {o}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="bg-white border border-[#E7E5E0] rounded-2xl p-6 space-y-4">
            <h2 className="font-semibold text-lg">Health & Lifestyle</h2>

            <div>
              <label className="text-xs font-semibold uppercase">
                Ethnicity <span className="text-red-500">*</span>
              </label>
              <select
                value={ethnicity}
                onChange={(e) => setEthnicity(e.target.value)}
                className="neo-input mt-1"
              >
                <option value="">Select</option>
                {[
                  "Asian",
                  "Black / African",
                  "Caucasian / White",
                  "Hispanic / Latino",
                  "Middle Eastern",
                  "Native American",
                  "Pacific Islander",
                  "South Asian",
                  "Southeast Asian",
                  "Mixed / Other",
                ].map((e) => (
                  <option key={e} value={e}>
                    {e}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-xs font-semibold uppercase">
                Education
              </label>
              <select
                value={education}
                onChange={(e) => setEducation(e.target.value)}
                className="neo-input mt-1"
              >
                <option value="">Select</option>
                <option>High School</option>
                <option>Diploma</option>
                <option>Bachelor</option>
                <option>Master</option>
                <option>PhD</option>
              </select>
            </div>

            <div className="flex gap-2">
              <div className="flex-1">
                <label className="text-xs font-semibold uppercase">Kids</label>
                <select
                  value={kids}
                  onChange={(e) => setKids(e.target.value)}
                  className="neo-input mt-1"
                >
                  <option value="">Kids?</option>
                  <option value="have">I have kids</option>
                  <option value="none">No kids</option>
                </select>
              </div>
              <div className="flex-1">
                <label className="text-xs font-semibold uppercase">
                  Want Kids
                </label>
                <select
                  value={wantKids}
                  onChange={(e) => setWantKids(e.target.value)}
                  className="neo-input mt-1"
                >
                  <option value="">Want kids?</option>
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                  <option value="maybe">Maybe</option>
                </select>
              </div>
            </div>

            <div>
              <label className="text-xs font-semibold uppercase">Smoking</label>
              <select
                value={smoke}
                onChange={(e) => setSmoke(e.target.value)}
                className="neo-input mt-1"
              >
                <option value="">Smoking?</option>
                <option value="yes">Smokes</option>
                <option value="no">Non-smoker</option>
                <option value="sometimes">Sometimes</option>
              </select>
            </div>

            <div>
              <label className="text-xs font-semibold uppercase">Drinking</label>
              <select
                value={drink}
                onChange={(e) => setDrink(e.target.value)}
                className="neo-input mt-1"
              >
                <option value="">Drinking?</option>
                <option value="yes">Drinks</option>
                <option value="no">Non-drinker</option>
                <option value="socially">Socially</option>
              </select>
            </div>

            <div>
              <label className="text-xs font-semibold uppercase">
                Employment
              </label>
              <select
                value={employment}
                onChange={(e) => setEmployment(e.target.value)}
                className="neo-input mt-1"
              >
                <option value="">Select</option>
                <option value="employed">Employed</option>
                <option value="unemployed">Unemployed</option>
                <option value="student">Student</option>
                <option value="self">Self-employed</option>
              </select>
            </div>

            <div>
              <label className="text-xs font-semibold uppercase">
                Positive Since
              </label>
              <input
                value={positiveSince}
                onChange={(e) => setPositiveSince(e.target.value)}
                className="neo-input mt-1"
                placeholder="e.g. 2020"
              />
            </div>

            <div>
              <label className="text-xs font-semibold uppercase">Height</label>
              <input
                value={height}
                onChange={(e) => setHeight(e.target.value)}
                className="neo-input mt-1"
                placeholder='e.g. 5&apos;10"'
              />
            </div>

            <div>
              <label className="text-xs font-semibold uppercase">Religion</label>
              <input
                value={religion}
                onChange={(e) => setReligion(e.target.value)}
                className="neo-input mt-1"
                placeholder="e.g. Christian"
              />
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="bg-white border border-[#E7E5E0] rounded-2xl p-6 space-y-4">
            <h2 className="font-semibold text-lg">Appearance & Discovery</h2>

            <div>
              <label className="text-xs font-semibold uppercase">
                Display Name
              </label>
              <input
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                className="neo-input mt-1"
                placeholder="How you'll appear"
              />
            </div>

            <div>
              <label className="text-xs font-semibold uppercase">Bio</label>
              <textarea
                value={bio}
                onChange={(e) => setBio(e.target.value)}
                className="neo-input mt-1 h-20 resize-none"
                placeholder="Tell others about yourself"
                maxLength={500}
              />
            </div>

            <div>
              <label className="text-xs font-semibold uppercase">
                Interests
              </label>
              <input
                value={interests}
                onChange={(e) => setInterests(e.target.value)}
                className="neo-input mt-1"
                placeholder="Travel, Music, Fitness (comma separated)"
              />
            </div>

            <div>
              <label className="text-xs font-semibold uppercase">
                Looking For
              </label>
              <input
                value={lookingFor}
                onChange={(e) => setLookingFor(e.target.value)}
                className="neo-input mt-1"
                placeholder="Friendship, Dating"
              />
            </div>

            <div>
              <label className="text-xs font-semibold uppercase">
                Profile Image
              </label>
              <ImageUpload
                images={profileImage ? [profileImage] : []}
                onChange={(imgs) => setProfileImage(imgs[0] || "")}
                maxImages={1}
                maxSizeMB={10}
              />
            </div>

            <div>
              <label className="text-xs font-semibold uppercase">
                Gallery (up to 5)
              </label>
              <ImageUpload
                images={galleryImages}
                onChange={setGalleryImages}
                maxImages={5}
                maxSizeMB={10}
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold uppercase">
                  Preferred Gender
                </label>
                <select
                  value={prefGender}
                  onChange={(e) => {
                    setPrefGender(e.target.value);
                    setPrefGenderUserOverridden(true);
                  }}
                  className="neo-input mt-1"
                >
                  <option value="">Any</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase">
                  Age Range
                </label>
                <div className="flex gap-2 items-center">
                  <input
                    type="number"
                    value={prefMinAge}
                    onChange={(e) =>
                      setPrefMinAge(parseInt(e.target.value) || 18)
                    }
                    className="neo-input w-16"
                    min={18}
                    max={100}
                  />
                  <span>-</span>
                  <input
                    type="number"
                    value={prefMaxAge}
                    onChange={(e) =>
                      setPrefMaxAge(parseInt(e.target.value) || 99)
                    }
                    className="neo-input w-16"
                    min={18}
                    max={100}
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {step === 4 && (
          <div className="bg-white border border-[#E7E5E0] rounded-2xl p-6 space-y-5">
            <div className="text-center">
              <div className="w-12 h-12 rounded-full bg-green-50 border border-green-200 flex items-center justify-center mx-auto mb-3">
                <Phone className="w-6 h-6 text-green-500" />
              </div>
              <h2 className="font-semibold text-lg">
                Add Your WhatsApp Number
              </h2>
              <p className="text-sm text-[#6B6B70] mt-1">
                Optional, but highly recommended
              </p>
            </div>

            <div className="bg-[#F5F3EE] border border-[#E7E5E0] rounded-xl p-4 space-y-2 text-sm text-[#404040]">
              <div className="flex items-start gap-2">
                <ShieldCheck className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                <span>
                  Your number is <strong>never shown</strong> on your profile.
                  It is only used to let matches message you outside the app
                  when you're not online.
                </span>
              </div>
              <div className="flex items-start gap-2">
                <MessageCircle className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                <span>
                  When you match with someone, they can tap a button to start a
                  WhatsApp chat with you – no need to exchange numbers
                  manually.
                </span>
              </div>
              <p className="text-xs text-[#6B6B70] mt-2">
                You can choose who sees your WhatsApp number later in
                Visibility settings.
              </p>
            </div>

            <div className="space-y-3">
              <label className="text-xs font-semibold uppercase">
                Country Code
              </label>
              <select
                value={countryCode}
                onChange={(e) => {
                  setCountryCode(e.target.value);
                  setPhoneError("");
                }}
                className="neo-input w-full"
              >
                {COUNTRY_CODES.map((c) => (
                  <option key={c.code + c.name} value={c.code}>
                    {c.flag} {c.name} ({c.code})
                  </option>
                ))}
              </select>

              <label className="text-xs font-semibold uppercase">
                Phone Number
              </label>
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-500">
                  {countryCode}
                </span>
                <input
                  type="tel"
                  value={phoneNumber}
                  onChange={(e) => {
                    setPhoneNumber(e.target.value.replace(/[^\d\s]/g, ""));
                    setPhoneError("");
                  }}
                  placeholder="e.g. 82 123 4567"
                  className="neo-input flex-1"
                  maxLength={15}
                />
              </div>
              {phoneError && (
                <p className="text-xs text-red-500">{phoneError}</p>
              )}
              <p className="text-xs text-[#6B6B70]">
                We'll store it as {countryCode}
                {phoneNumber ? phoneNumber.replace(/\D/g, "") : ""}
              </p>
            </div>

            <div className="flex gap-3 pt-4">
              <button
                onClick={skipWhatsApp}
                disabled={saving}
                className="neo-btn neo-btn-secondary flex-1"
              >
                <SkipForward className="w-4 h-4" /> Skip for now
              </button>
              <button
                onClick={() => handleSubmit(false)}
                disabled={saving}
                className="neo-btn bg-green-600 border-green-600 flex-1"
              >
                {saving ? "Saving..." : "Complete Profile"}
              </button>
            </div>
          </div>
        )}

        {step < STEPS.length - 1 && (
          <div className="flex justify-between mt-6">
            <button
              onClick={handleBack}
              disabled={step === 0}
              className="neo-btn neo-btn-secondary"
            >
              <ArrowLeft className="w-4 h-4" /> Back
            </button>
            <button
              onClick={handleNext}
              disabled={step === 0 && !locationSet}
              className="neo-btn bg-purple-600 border-purple-600"
            >
              Next <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Safety-net consent dialog — rarely triggered in normal flow */}
        <AlertDialog
          open={consentDialogOpen}
          onOpenChange={(open) => {
            if (!consentSubmitting) setConsentDialogOpen(open);
          }}
        >
          <AlertDialogContent className="bg-white rounded-2xl shadow-xl border border-[#E7E5E0] max-w-md">
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2 text-[#0F0F10]">
                <ShieldCheck className="w-5 h-5 text-purple-600" />
                Health-status consent required
              </AlertDialogTitle>
              <AlertDialogDescription className="text-[#404040] text-sm leading-relaxed">
                {consentErrorMessage || (
                  <>
                    Your health-status consent wasn't recorded. Haven requires
                    this to match you with others in the community.
                    <br />
                    <br />
                    Under POPIA sections 26–27, we need explicit consent to
                    process your health status. Without it, you cannot complete
                    profile setup.
                    <br />
                    <br />
                    Your status is only shown to matches you approve — never
                    publicly, never to advertisers. You can withdraw consent at
                    any time by clearing your health status in your profile.
                  </>
                )}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel
                disabled={consentSubmitting}
                className="border-[#E7E5E0]"
              >
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={(e) => {
                  e.preventDefault();
                  handleConsentAccept();
                }}
                disabled={consentSubmitting}
                className="bg-purple-600 text-white hover:bg-purple-700"
              >
                {consentSubmitting ? "Saving…" : "I consent"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </main>
    </div>
  );
}